<template>
  <div
    class="flex items-start justify-between gap-4"
    data-testid="cart-partners-points"
  >
    <p>
      <i18n-t
        :keypath="pointsKeypath"
        :plural="basketPoints.points"
        tag="span"
      >
        <template #points><strong>{{ $n(basketPoints.points) }}</strong></template>
        <template #level>{{ basketPoints.reachedLevelNumber }}</template>
        <template #percentage>{{ levelDiscountPercentage }}</template>
      </i18n-t>
      <template v-if="basketPoints.nextLevel">
        {{ ' · ' }}
        <i18n-t
          :keypath="nextLevelKeypath"
          :plural="basketPoints.nextLevel.missingPoints"
          tag="span"
        >
          <template #points><strong>{{ $n(basketPoints.nextLevel.missingPoints) }}</strong></template>
          <template #level>{{ basketPoints.nextLevel.levelNumber }}</template>
          <template #percentage>{{ levelDiscountPercentage }}</template>
        </i18n-t>
      </template>
    </p>
    <CustomLink
      :url="dashboardUrl"
      :type="LINK_TYPE.NoStyle"
      blank
      class="shrink-0 text-xs text-EXPONDO-grey underline"
      data-testid="cart-partners-dashboard-link"
    >
      {{ $t('partners-basket-dashboard-link') }} ↗
    </CustomLink>
  </div>
</template>

<script lang="ts">

import CustomLink from '~/components/atoms/Link/CustomLink.vue';
import { ROUTES } from '~~/constants/routes';
import type { BasketPartnersPoints } from '~~/helpers/loyaltyPoints/getBasketPartnersPoints';
import { LINK_TYPE } from '~~/types/components/Link/LinkType';

export default defineComponent({
  name: 'CartPartnersPoints',
  components: {
    CustomLink,
  },
  props: {
    basketPoints: {
      type: Object as PropType<BasketPartnersPoints>,
      required: true,
    },
    // adds the discount of the level the message mentions
    showsLevelDiscount: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {

    const { getUrlByRouteKey } = useRouteExtended();
    const { loyaltyLevels } = useCTLoyaltyLevels();

    const levelDiscountPercentage = computed<number | null>(() => {

      const levelNumber = props.basketPoints.reachedLevelNumber ?? props.basketPoints.nextLevel?.levelNumber;

      if (!props.showsLevelDiscount || !levelNumber) {

        return null;

      }

      return loyaltyLevels.value?.find(level => level.levelNumber === levelNumber)?.discountPercentage || null;

    });

    const pointsKeypath = computed(() => {

      if (!props.basketPoints.reachedLevelNumber) {

        return 'partners-basket-points';

      }

      return levelDiscountPercentage.value ? 'partners-basket-reaches-level-discount' : 'partners-basket-reaches-level';

    });

    const nextLevelKeypath = computed(() =>
      levelDiscountPercentage.value ? 'partners-basket-points-to-level-discount' : 'partners-basket-points-to-level');

    return {
      LINK_TYPE,
      dashboardUrl: getUrlByRouteKey(ROUTES.MY_EXPONDO_PARTNERS),
      levelDiscountPercentage,
      pointsKeypath,
      nextLevelKeypath,
    };

  },
});

</script>
